import React, {useCallback, useState} from "react";
import Button from "../../../../../components/base/button/button";
import {Card} from "@mui/material";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from '../../../../../components/form';
import {usePartnerInformation, usePartnerInformationController} from "../../../../../hooks/use-partner-information";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import Grid2 from "@mui/material/Unstable_Grid2";


const EditPartnerGeneralInformationSection = () => {
    const partnerInformation = usePartnerInformation();
    const {setCachedPartnerInformation} = usePartnerInformationController();
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const schema = React.useMemo(() => Yup.object().shape({
        name: Yup.string().trim().nullable(),
        description: Yup.string().trim().nullable().nullable(),
        website: Yup.string().trim().url(ValidateMessages.incorrectType("Website")).nullable(),
        phone: Yup.string().trim().nullable(),
        fax: Yup.string().trim().nullable(),
    }), []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = React.useMemo(() => ({
        name: partnerInformation?.name ?? "",
        description: partnerInformation?.description ?? "",
        website: partnerInformation?.website ?? "",
        phone: partnerInformation?.phone ?? "",
        fax: partnerInformation?.fax ?? "",
    }), [partnerInformation]);

    /**
     * Handles submission of the profile information changes:
     * Calls the api regarding this action
     * if the api call was not successful, shows an error dialog, otherwise, sets the current information to the caching service
     * @param values
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        const response = await Api.updatePartnerBasicInfo({
            name: values?.name ?? "",
            description: values?.description ?? "",
            website: values?.website ?? "",
            phone: values?.phone ?? "",
            fax: values?.fax ?? "",
            updatedProperties: ["Name", "Description", "Website", "Phone", "Fax"],
        });
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            setCachedPartnerInformation(prevState => ({
                ...prevState,
                name: values?.name ?? "",
                description: values?.description ?? "",
                website: values?.website ?? "",
                phone: values?.phone ?? "",
                fax: values?.fax ?? "",
            }));
        }
    }, [isMounted, setCachedPartnerInformation]);

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3}}>
                {
                    !partnerInformation &&
                    <div className={'loading current-operator-profile'}>
                        <div/>
                    </div>
                }
                {
                    !!partnerInformation &&
                    <Form
                        formProps={{className: '100%'}}
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                    >
                        {({pristine}) => (
                            <Grid2 container spacing={3}>
                                <Grid2 xs={12}>
                                    <p className={'text-lg font-weight-semi-bold text-color-1'}>
                                        Basic Org Information
                                    </p>
                                </Grid2>

                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Name'}
                                        name={'name'}
                                        required={required.name}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Description'}
                                        name={'description'}
                                        required={required.description}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Website'}
                                        name={'website'}
                                        required={required.website}
                                    />
                                </Grid2>
                                <Grid2 xs={12} sm={6} md={12} lg={6}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Phone'}
                                        name={'phone'}
                                        required={required.phone}
                                    />
                                </Grid2>
                                <Grid2 xs={12} sm={6} md={12} lg={6}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Fax'}
                                        name={'fax'}
                                        required={required.fax}
                                    />
                                </Grid2>
                                <Grid2 xs={12} sx={{marginBlockStart: 2}}>
                                    <Button
                                        disabled={!!submitting || pristine}
                                        type={'submit'}
                                        variant={'contained'}
                                        color={'primary'}
                                        size={'medium'}
                                        sx={{minWidth: 200, marginInline: 'auto', display: 'flex'}}
                                    >
                                        {submitting ? 'Submitting...' : 'Save'}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        )}
                    </Form>
                }
            </Card>
        </>
    );
}

export default EditPartnerGeneralInformationSection;
